import { useState } from 'react';
import {
  Activity,
  Search,
  Filter,
  User,
  Settings,
  FileText,
  Trash2,
  Edit2,
  LogIn,
  LogOut,
  Plus,
  Eye,
  Calendar,
  Clock,
  Download,
} from 'lucide-react';
import { cn } from '@/lib/cn';

interface ActivityLog {
  id: string;
  user: string;
  action: string;
  target: string;
  type: 'auth' | 'content' | 'settings' | 'user' | 'system';
  timestamp: Date;
  ip?: string;
  details?: string;
}

// Demo data
const initialLogs: ActivityLog[] = [
  { id: '1', user: 'Admin', action: 'Giris yapti', target: 'Sistem', type: 'auth', timestamp: new Date(), ip: '192.168.1.100' },
  { id: '2', user: 'Admin', action: 'Blog yazisi ekledi', target: 'Yapay Zeka Trendleri 2024', type: 'content', timestamp: new Date(Date.now() - 1800000) },
  { id: '3', user: 'Admin', action: 'Ayarlari guncelledi', target: 'SEO Ayarlari', type: 'settings', timestamp: new Date(Date.now() - 3600000) },
  { id: '4', user: 'Admin', action: 'Mesaji arsivledi', target: 'Iletisim #123', type: 'content', timestamp: new Date(Date.now() - 7200000) },
  { id: '5', user: 'Admin', action: 'Proje durumunu guncelledi', target: 'XYZ Holding Projesi', type: 'content', timestamp: new Date(Date.now() - 14400000) },
  { id: '6', user: 'Admin', action: 'Medya yukledi', target: 'hero-image.jpg', type: 'content', timestamp: new Date(Date.now() - 28800000) },
  { id: '7', user: 'System', action: 'Yedekleme tamamlandi', target: 'Veritabani', type: 'system', timestamp: new Date(Date.now() - 43200000) },
  { id: '8', user: 'Admin', action: 'Cikis yapti', target: 'Sistem', type: 'auth', timestamp: new Date(Date.now() - 86400000), ip: '192.168.1.100' },
  { id: '9', user: 'Admin', action: 'Giris yapti', target: 'Sistem', type: 'auth', timestamp: new Date(Date.now() - 86400000), ip: '192.168.1.100' },
  { id: '10', user: 'Admin', action: 'Icerik duzenledi', target: 'Ana Sayfa Hero', type: 'content', timestamp: new Date(Date.now() - 172800000) },
];

const actionTypes = [
  { value: 'all', label: 'Tum Islemler' },
  { value: 'auth', label: 'Oturum' },
  { value: 'content', label: 'Icerik' },
  { value: 'settings', label: 'Ayarlar' },
  { value: 'user', label: 'Kullanici' },
  { value: 'system', label: 'Sistem' },
];

export function AdminActivity() {
  const [logs, setLogs] = useState<ActivityLog[]>(initialLogs);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  // Filter logs
  const filteredLogs = logs.filter(log => {
    const matchesSearch = log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.target.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || log.type === typeFilter;

    let matchesDate = true;
    if (dateFilter === 'today') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      matchesDate = log.timestamp >= today;
    } else if (dateFilter === 'week') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      matchesDate = log.timestamp >= weekAgo;
    } else if (dateFilter === 'month') {
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      matchesDate = log.timestamp >= monthAgo;
    }

    return matchesSearch && matchesType && matchesDate;
  });

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'auth': return <LogIn size={16} />;
      case 'content': return <FileText size={16} />;
      case 'settings': return <Settings size={16} />;
      case 'user': return <User size={16} />;
      case 'system': return <Activity size={16} />;
      default: return <Activity size={16} />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'auth': return 'bg-blue-500/10 text-blue-500';
      case 'content': return 'bg-green-500/10 text-green-500';
      case 'settings': return 'bg-purple-500/10 text-purple-500';
      case 'user': return 'bg-orange-500/10 text-orange-500';
      case 'system': return 'bg-gray-500/10 text-gray-400';
      default: return 'bg-gray-500/10 text-gray-400';
    }
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return 'Az once';
    if (minutes < 60) return `${minutes} dakika once`;
    if (hours < 24) return `${hours} saat once`;
    if (days < 7) return `${days} gun once`;
    return date.toLocaleDateString('tr-TR');
  };

  const stats = {
    today: logs.filter(l => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return l.timestamp >= today;
    }).length,
    auth: logs.filter(l => l.type === 'auth').length,
    content: logs.filter(l => l.type === 'content').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Aktivite Loglari</h1>
          <p className="text-text-secondary">Sistem aktivitelerini ve kullanici islemlerini takip edin</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border-default text-text-secondary hover:text-text-primary hover:border-accent transition-colors">
          <Download size={18} /> Disa Aktar
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-accent">{stats.today}</p>
          <p className="text-sm text-text-secondary">Bugun</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-blue-500">{stats.auth}</p>
          <p className="text-sm text-text-secondary">Oturum Islemi</p>
        </div>
        <div className="bg-bg-card border border-border-default rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-500">{stats.content}</p>
          <p className="text-sm text-text-secondary">Icerik Islemi</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" size={18} />
          <input
            type="text"
            placeholder="Aktivite ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
        >
          {actionTypes.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="px-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
        >
          <option value="all">Tum Zamanlar</option>
          <option value="today">Bugun</option>
          <option value="week">Bu Hafta</option>
          <option value="month">Bu Ay</option>
        </select>
      </div>

      {/* Activity Timeline */}
      <div className="bg-bg-card border border-border-default rounded-xl overflow-hidden">
        <div className="divide-y divide-border-default">
          {filteredLogs.map((log, index) => (
            <div key={log.id} className="p-4 flex items-start gap-4 hover:bg-bg-elevated/50 transition-colors">
              <div className={cn('p-2 rounded-lg', getTypeColor(log.type))}>
                {getTypeIcon(log.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-text-primary">{log.user}</span>
                  <span className="text-text-secondary">{log.action}</span>
                </div>
                <p className="text-sm text-accent truncate">{log.target}</p>
                {log.ip && (
                  <p className="text-xs text-text-tertiary mt-1">IP: {log.ip}</p>
                )}
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm text-text-tertiary">{formatTime(log.timestamp)}</p>
                <p className="text-xs text-text-tertiary">
                  {log.timestamp.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))}
        </div>

        {filteredLogs.length === 0 && (
          <div className="text-center py-12">
            <Activity className="mx-auto mb-4 text-text-tertiary" size={48} />
            <p className="text-text-tertiary">Aktivite bulunamadi</p>
          </div>
        )}
      </div>

      {/* Load More */}
      {filteredLogs.length > 0 && (
        <div className="text-center">
          <button className="px-6 py-2 rounded-lg border border-border-default text-text-secondary hover:text-text-primary hover:border-accent transition-colors">
            Daha Fazla Yukle
          </button>
        </div>
      )}
    </div>
  );
}
